// RHF migration: form fields (replyInput, markAsBenchmark) are owned by parent SessionsTab
// via props; internal useState here is UI-only (expanded chunks, summary overrides,
// refresh flag) and not form state — leaving as useState per migration guideline #10.
import { errorToast } from "@/shared/lib/apiErrors";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Loader2, RefreshCw, Send, X } from "lucide-react";
import { toast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Checkbox } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  aiKefuService,
  type CsMessageListItem,
  type CsSessionDetail,
  type FeedbackSummary,
  type AnswerMedia,
  type AnswerSource,
  type SourceChunk,
} from "../../api/aiKefuService";

import { FeedbackOverall, SourceFeedbackButtons } from "./FeedbackBar";
import { SESSION_STATUS_KEYS, channelLabel, messageRoleLabelKey } from "./constants";
import { isWecomChannel } from "../../model/kefuRealtime";

type ExpandedState =
  | { status: "loading" }
  | {
      status: "loaded";
      sourceChunks: SourceChunk[];
      sources: AnswerSource[];
      media: AnswerMedia[];
    }
  | { status: "error" };

type Props = {
  sessionId: string;
  detail: CsSessionDetail | undefined;
  messages: CsMessageListItem[];
  onLoadEarlier: () => void;
  hasEarlier: boolean;
  isLoadingEarlier: boolean;
  onClose: () => void;
  onRefresh: () => void;
  replyInput: string;
  onReplyInputChange: (v: string) => void;
  onSendReply: () => void;
  onReplyKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  isReplying: boolean;
  markAsBenchmark: boolean;
  onMarkAsBenchmarkChange: (value: boolean) => void;
};

// 企微客服渠道判定(2026-08-07 事故产物)见 model/kefuRealtime#isWecomChannel:
// 后端 `channel_type` 落的是 `wecom_kf`,而部分 DTO 投影成 `wecom` —— 两种都要认(判宽不判窄)。
// ★1010 一期补完:此前客户档案块写的是 `detail?.channel === "wecom"`,后端实际回 wecom_kf → 档案块从来不显示。

export function SessionDetailCard({
  sessionId,
  detail,
  messages,
  onLoadEarlier,
  hasEarlier,
  isLoadingEarlier,
  onClose,
  onRefresh,
  replyInput,
  onReplyInputChange,
  onSendReply,
  onReplyKeyDown,
  isReplying,
  markAsBenchmark,
  onMarkAsBenchmarkChange,
}: Props) {
  const { t } = useTranslation("customer-service");
  const { user } = useAuth();
  const canReply = isTenantManagerPlus(user?.role);
  const ref = useRef<HTMLDivElement>(null);
  const [summaryOverrides, setSummaryOverrides] = useState<Record<string, FeedbackSummary>>({});
  const [expanded, setExpanded] = useState<Record<string, ExpandedState>>({});
  const [isRefreshingCustomer, setIsRefreshingCustomer] = useState(false);

  async function handleRefreshCustomer() {
    setIsRefreshingCustomer(true);
    try {
      await aiKefuService.refreshCustomer(sessionId);
      toast({ title: t("sessionDetail.toastIdentityRefreshed") });
      // 触发详情整体刷新,把新 customer 字段拉回来
      onRefresh();
    } catch (err) {
      toast(errorToast(err, { fallbackTitle: t("sessionDetail.toastRefreshFailed") }));
    } finally {
      setIsRefreshingCustomer(false);
    }
  }

  function genderLabel(gender: number | null | undefined): string {
    if (gender === 1) return t("sessionDetail.genderMale");
    if (gender === 2) return t("sessionDetail.genderFemale");
    return t("sessionDetail.genderUnknown");
  }

  useEffect(() => {
    if (sessionId) {
      setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
    setSummaryOverrides({});
    setExpanded({});
  }, [sessionId]);

  async function toggleDetail(messageId: string) {
    if (expanded[messageId]) {
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });
      return;
    }
    setExpanded((prev) => ({ ...prev, [messageId]: { status: "loading" } }));
    try {
      const detail = await aiKefuService.getMessageDetail(sessionId, messageId);
      setExpanded((prev) => ({
        ...prev,
        [messageId]: {
          status: "loaded",
          sourceChunks: detail.sourceChunks ?? [],
          sources: detail.sources ?? [],
          media: detail.media ?? [],
        },
      }));
    } catch {
      setExpanded((prev) => ({ ...prev, [messageId]: { status: "error" } }));
      toast({ title: t("sessionDetail.toastLoadSourceFailed"), variant: "destructive" });
    }
  }

  return (
    <Card ref={ref} className="shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="section-title">
            {t("sessionDetail.titlePrefix")} · {sessionId}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-1" />
              {t("sessionDetail.refresh")}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isWecomChannel(detail?.channel) && detail?.externalUserid ? (
          <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
            {detail.customer?.avatar ? (
              <img
                src={detail.customer.avatar}
                alt={detail.customer.nickname ?? t("sessionDetail.customerAvatarAlt")}
                className="h-12 w-12 rounded-full object-cover"
              />
            ) : (
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center text-xl text-muted-foreground">
                {(detail.customer?.nickname ?? "?").charAt(0)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">
                {detail.customer?.nickname ?? t("sessionDetail.unknownCustomer")}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {genderLabel(detail.customer?.gender)} ·{" "}
                <span className="font-mono">{detail.externalUserid}</span>
                {detail.customer?.lastSyncedAt ? (
                  <span className="ml-2">
                    {t("sessionDetail.lastSynced")}{" "}
                    {new Date(detail.customer.lastSyncedAt).toLocaleString("zh-CN")}
                  </span>
                ) : (
                  <span className="ml-2 text-warning">{t("sessionDetail.notSynced")}</span>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshCustomer}
              disabled={isRefreshingCustomer}
            >
              {isRefreshingCustomer ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
              )}
              {t("sessionDetail.refreshIdentity")}
            </Button>
          </div>
        ) : null}
        {/* 1010 一期补完 B6:网页 / H5 访客没有企微档案 —— 显示接入点、落地页、留资信息 */}
        {detail && !isWecomChannel(detail.channel) && detail.visitor ? (
          <div
            className="rounded-md border bg-muted/30 p-3 text-sm space-y-1.5"
            data-testid="session-visitor-block"
          >
            <div className="flex items-center gap-1.5 font-medium">
              <Globe className="h-4 w-4 text-primary" />
              {t("sessionDetail.visitorTitle")}
            </div>
            <div className="grid gap-x-4 gap-y-1 md:grid-cols-2 text-xs">
              <div>
                <span className="text-muted-foreground">{t("sessionDetail.visitorEntry")}</span>
                {detail.visitor.entryName || detail.entryName || "-"}
              </div>
              <div className="min-w-0 truncate">
                <span className="text-muted-foreground">{t("sessionDetail.visitorLanding")}</span>
                {detail.visitor.landingPageUrl ? (
                  <a
                    href={detail.visitor.landingPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                    title={detail.visitor.landingPageUrl}
                  >
                    {detail.visitor.landingPageTitle || detail.visitor.landingPageUrl}
                  </a>
                ) : (
                  detail.visitor.landingPageTitle || "-"
                )}
              </div>
            </div>
            {detail.visitor.leadPhone ? (
              <div className="grid gap-x-4 gap-y-1 md:grid-cols-2 text-xs border-t pt-1.5">
                <div>
                  <span className="text-muted-foreground">{t("sessionDetail.leadPhone")}</span>
                  <span className="font-mono">{detail.visitor.leadPhone}</span>
                </div>
                {detail.visitor.leadName && (
                  <div>
                    <span className="text-muted-foreground">{t("sessionDetail.leadName")}</span>
                    {detail.visitor.leadName}
                  </div>
                )}
                {detail.visitor.leadWechat && (
                  <div>
                    <span className="text-muted-foreground">{t("sessionDetail.leadWechat")}</span>
                    {detail.visitor.leadWechat}
                  </div>
                )}
                {detail.visitor.leadAt && (
                  <div>
                    <span className="text-muted-foreground">{t("sessionDetail.leadAt")}</span>
                    {new Date(detail.visitor.leadAt).toLocaleString("zh-CN", { hour12: false })}
                  </div>
                )}
                {detail.visitor.leadNote && (
                  <div className="md:col-span-2 whitespace-pre-wrap">
                    <span className="text-muted-foreground">{t("sessionDetail.leadNote")}</span>
                    {detail.visitor.leadNote}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground border-t pt-1.5">
                {t("sessionDetail.leadNone")}
              </div>
            )}
          </div>
        ) : null}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">{t("sessionDetail.labelAssignee")}</span>
            {detail?.assigneeName || t("sessionDetail.assigneeNone")}
          </div>
          <div>
            <span className="text-muted-foreground">{t("sessionDetail.labelStatus")}</span>
            {detail?.status
              ? SESSION_STATUS_KEYS[detail.status]
                ? t(SESSION_STATUS_KEYS[detail.status].labelKey)
                : detail.status
              : "-"}
          </div>
          <div>
            <span className="text-muted-foreground">{t("sessionDetail.labelChannel")}</span>
            {detail?.channel ? channelLabel(t, detail.channel, detail.entryName) : "-"}
          </div>
          {isWecomChannel(detail?.channel) && (
            <div>
              <span className="text-muted-foreground">{t("sessionDetail.labelKfAccount")}</span>
              {detail?.kfName || "-"}
            </div>
          )}
        </div>
        <div className="space-y-3">
          {hasEarlier && (
            <div className="text-center">
              <Button
                variant="outline"
                size="sm"
                onClick={onLoadEarlier}
                disabled={isLoadingEarlier}
                className="text-xs"
              >
                {isLoadingEarlier ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                {t("sessionDetail.loadEarlier")}
              </Button>
            </div>
          )}
          {messages.length === 0 && (
            <div className="text-sm text-muted-foreground">{t("sessionDetail.emptyMessages")}</div>
          )}
          {messages.map((message, idx) => {
            const msgId = message.messageId;
            // 后端/DB 的 AI 角色值是 "ai"(cs_message.role = customer|ai|human),不是 "assistant"。
            // 此前写死 "assistant" → 反馈工具栏对所有 AI 消息永不渲染(命中片段/点赞踩闭环失效)。
            const isAssistant = message.messageRole === "ai" || message.messageRole === "assistant";
            const expState = msgId ? expanded[msgId] : undefined;
            const docCount = message.sourceDocIds?.length ?? 0;

            return (
              <div
                key={message.id}
                className="rounded-lg border p-3 space-y-2 fx-enter"
                style={{ animationDelay: `${Math.min(idx, 7) * 30}ms` }}
              >
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span data-testid="message-role">
                    {messageRoleLabelKey(message.messageRole)
                      ? t(messageRoleLabelKey(message.messageRole)!)
                      : message.messageRole}
                  </span>
                  <span>
                    {message.createdAt
                      ? new Date(message.createdAt).toLocaleString("zh-CN", { hour12: false })
                      : "-"}
                  </span>
                </div>
                <div className="text-sm whitespace-pre-wrap">{message.content}</div>

                {isAssistant && msgId && (
                  <div className="flex items-center justify-between gap-3 pt-1.5 border-t border-border/60">
                    <div className="flex items-center gap-2 min-w-0">
                      {/* 赞/踩写反馈仅 MANAGER+(后端 feedback=MANAGER;STAFF 只读队列,点击必 403）。
                          展开来源/引用数等只读元素不门控,STAFF 仍可查看。audit-0713 Batch1 D5-5。 */}
                      {canReply && (
                        <FeedbackOverall
                          sessionId={sessionId}
                          messageId={msgId}
                          summary={summaryOverrides[msgId] ?? message.feedbackSummary ?? null}
                          onUpdated={(next) =>
                            setSummaryOverrides((s) => ({ ...s, [msgId]: next }))
                          }
                        />
                      )}
                      {docCount > 0 && (
                        <span className="text-2xs text-muted-foreground/80">
                          {t("sessionDetail.citationCount", { count: docCount })}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="text-2xs text-muted-foreground hover:text-primary transition-colors"
                      onClick={() => toggleDetail(msgId)}
                    >
                      {expState ? t("sessionDetail.collapse") : t("sessionDetail.expand")}
                    </button>
                  </div>
                )}

                {expState?.status === "loading" && (
                  <div className="text-xs text-muted-foreground">{t("sessionDetail.loading")}</div>
                )}

                {expState?.status === "error" && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-destructive">{t("sessionDetail.loadFailed")}</span>
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => msgId && toggleDetail(msgId)}
                    >
                      {t("sessionDetail.retry")}
                    </button>
                  </div>
                )}

                {/* ★G1a:「发给客户的图」排在最前 —— 它不是溯源材料,是**AI 对客户做过的事**。
                    图是另一条消息发出去的,光看上面的 answer 文本完全看不出来。审计工作台若不显示,
                    「这张内部图怎么发出去的」就无从查起。 */}
                {expState?.status === "loaded" && expState.media.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-2xs font-medium uppercase tracking-wide text-muted-foreground/70">
                      已发给客户的图
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {expState.media.map((m, index) => (
                        <div
                          key={`${m.assetRef}#${index}`}
                          className="w-28 overflow-hidden rounded-md border bg-muted/40"
                        >
                          {m.previewUrl ? (
                            <img
                              src={m.previewUrl}
                              alt={m.caption || m.assetRef}
                              loading="lazy"
                              className="h-20 w-full object-cover"
                            />
                          ) : (
                            // 签不出预览(对象已删/桶认不出)——「图发过」是既成事实,不能因为
                            // 预览没了就让这条记录从审计里消失。
                            <div className="flex h-20 w-full items-center justify-center px-1 text-center text-2xs text-muted-foreground">
                              图已不可预览
                            </div>
                          )}
                          <div
                            className="truncate px-1.5 py-1 text-2xs text-muted-foreground"
                            title={`${m.caption}\n${m.assetRef}`}
                          >
                            {m.caption || m.assetRef}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ★E2:答案来源摆在命中片段**前面** —— 审阅人第一眼要回答的是"这条答案凭什么
                    这么说",而命中片段是"召回了什么"(over-fetch,几乎不空)。把 over-fetch 的
                    列表摆在前面,人得自己从里面猜哪几篇真被用上了。 */}
                {expState?.status === "loaded" && expState.sources.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-2xs font-medium uppercase tracking-wide text-muted-foreground/70">
                      答案来源(真实引用)
                    </div>
                    {expState.sources.map((src, index) => (
                      <div
                        key={`${src.docId}#${src.page ?? index}`}
                        className="rounded-md border-l-2 border-primary/60 bg-primary/5 px-3 py-2 text-xs"
                      >
                        <div className="text-2xs text-muted-foreground truncate">
                          {src.docName}
                          {src.page ? ` · 第 ${src.page} 页` : ""}
                        </div>
                        {src.snippet && (
                          <div className="mt-1 italic text-muted-foreground">「{src.snippet}」</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {expState?.status === "loaded" && expState.sourceChunks.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-2xs font-medium uppercase tracking-wide text-muted-foreground/70">
                      {t("sessionDetail.sourceChunks")}
                    </div>
                    {expState.sourceChunks.map((chunk, index) => (
                      <div
                        key={`${chunk.docId}#${chunk.chunkIndex ?? index}`}
                        className="rounded-md border bg-muted/40 px-3 py-2 text-xs"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="min-w-0 flex-1 text-2xs text-muted-foreground truncate font-mono">
                            {chunk.docId}
                            {chunk.pageNumber ? ` · P.${chunk.pageNumber}` : ""}
                            {chunk.chunkIndex != null ? ` · #${chunk.chunkIndex}` : ""}
                          </div>
                          {msgId && canReply && (
                            <SourceFeedbackButtons
                              sessionId={sessionId}
                              messageId={msgId}
                              docId={chunk.docId}
                              chunkIndex={chunk.chunkIndex}
                              summary={summaryOverrides[msgId] ?? message.feedbackSummary ?? null}
                              onUpdated={(next) =>
                                setSummaryOverrides((s) => ({ ...s, [msgId]: next }))
                              }
                            />
                          )}
                        </div>
                        {chunk.answer ? (
                          <div className="space-y-1">
                            <div className="text-2xs text-muted-foreground/80">
                              {t("sessionDetail.hitQuestionLabel")}
                              {chunk.content}
                            </div>
                            <div className="whitespace-pre-wrap leading-relaxed">
                              {chunk.answer}
                            </div>
                          </div>
                        ) : (
                          <div className="whitespace-pre-wrap leading-relaxed">{chunk.content}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {expState?.status === "loaded" && expState.sourceChunks.length === 0 && (
                  <div className="text-xs text-muted-foreground">
                    {t("sessionDetail.noSourceChunks")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* ★ 2026-08-07 测试机实跑:企微渠道转人工 = 会话交给企微接待人员,我方从此发不出消息
            (必 95018)。产品设计本就是「转人工后我们这边只读」—— 人在企业微信客服工作台回。
            当时坐席在这里回了两句、两句都落了库、界面看着像发出去了,客户一个字没收到。
            所以这个输入框在企微渠道上不能只是"点了报错",而是根本不该出现。 */}
        {canReply && detail?.status === "human_handling" && isWecomChannel(detail?.channel) && (
          <div className="pt-3 border-t text-xs text-muted-foreground">
            企业微信渠道转人工后本平台只读 ——
            请在企业微信客服工作台回复客户；这里可查看完整聊天记录。
          </div>
        )}
        {/* audit-0713 C3b:reply 后端 MANAGER+,STAFF 不再渲染回复框(点了必 403) */}
        {canReply && detail?.status === "human_handling" && !isWecomChannel(detail?.channel) && (
          <div className="pt-3 border-t space-y-2">
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("sessionDetail.replyPlaceholder")}
                value={replyInput}
                onChange={(e) => onReplyInputChange(e.target.value)}
                onKeyDown={onReplyKeyDown}
                disabled={isReplying}
              />
              <Button size="sm" disabled={!replyInput.trim() || isReplying} onClick={onSendReply}>
                {isReplying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <div className="flex items-center gap-2 pl-1">
              <Checkbox
                id="mark-as-benchmark"
                checked={markAsBenchmark}
                onCheckedChange={(checked) => onMarkAsBenchmarkChange(checked === true)}
                disabled={isReplying}
              />
              <Label
                htmlFor="mark-as-benchmark"
                className="text-xs text-muted-foreground cursor-pointer"
              >
                {t("sessionDetail.markAsBenchmark")}
              </Label>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
